import React, { useMemo, useState } from 'react';
import { Box, MenuItem, Select, Switch, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { type DispatchDevice } from 'api/dispatch-monitor.api';
import { ThemePalette } from 'mui.theme';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const ACTION_CONFIG: Record<
  string,
  { labelKey: string; color: string; bg: string }
> = {
  PLAN_START: {
    labelKey: 'dm_log_action_plan_start',
    color: '#4ade80',
    bg: 'rgba(74,222,128,0.1)',
  },
  PLAN_COMPLETED: {
    labelKey: 'dm_log_action_completed',
    color: '#4ade80',
    bg: 'rgba(74,222,128,0.1)',
  },
  PLAN_ROUTE_CHANGED: {
    labelKey: 'dm_log_action_reroute',
    color: '#a5b4fc',
    bg: 'rgba(165,180,252,0.1)',
  },
  PLAN_ROUTE_CHANGE_IGNORED: {
    labelKey: 'dm_log_action_rte_ignored',
    color: '#6b7280',
    bg: 'rgba(107,114,128,0.1)',
  },
  PLAN_KPI_RECALCULATED: {
    labelKey: 'dm_log_action_kpi_update',
    color: '#60a5fa',
    bg: 'rgba(96,165,250,0.1)',
  },
  PLAN_PAYLOAD_UPDATED: {
    labelKey: 'dm_log_action_payload_upd',
    color: '#60a5fa',
    bg: 'rgba(96,165,250,0.1)',
  },
  BREAKDOWN: {
    labelKey: 'dm_log_action_breakdown',
    color: '#f87171',
    bg: 'rgba(248,113,113,0.12)',
  },
  CRITICAL_ALERT_ACKNOWLEDGE: {
    labelKey: 'dm_log_action_alert_ack',
    color: '#fb923c',
    bg: 'rgba(251,146,60,0.1)',
  },
  LOGIN: {
    labelKey: 'dm_log_action_login',
    color: '#9ca3af',
    bg: 'rgba(156,163,175,0.08)',
  },
  LOGOUT: {
    labelKey: 'dm_log_action_logout',
    color: '#9ca3af',
    bg: 'rgba(156,163,175,0.08)',
  },
  SHIFT_START: {
    labelKey: 'dm_log_action_shift_start',
    color: '#c084fc',
    bg: 'rgba(192,132,252,0.1)',
  },
  SHIFT_END: {
    labelKey: 'dm_log_action_shift_end',
    color: '#c084fc',
    bg: 'rgba(192,132,252,0.1)',
  },
  SESSION_CONTINUED: {
    labelKey: 'dm_log_action_session',
    color: '#6b7280',
    bg: 'rgba(107,114,128,0.08)',
  },
};

function formatTs(ts: number, tz: string): string {
  const ms = ts > 1e12 ? ts : ts * 1000;
  return dayjs(ms).tz(tz).format('DD MMM, HH:mm:ss');
}

interface Props {
  devices: Record<string, DispatchDevice>;
  truckNames: Record<string, string>;
  liveLogs: boolean;
  onToggle: (val: boolean) => void;
  enableDarkTheme: boolean;
  timezone: string;
}

const LogsPanel: React.FC<Props> = ({
  devices,
  truckNames,
  liveLogs,
  onToggle,
  enableDarkTheme,
  timezone,
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const [selectedAsset, setSelectedAsset] = useState<string>('all');

  const borderCol = palette.cardBorderColor;
  const textPrimary = enableDarkTheme ? '#e2e8f0' : '#1a202c';
  const textMuted = enableDarkTheme ? '#6b7280' : '#8A92A0';
  const cardBg = enableDarkTheme
    ? palette.boxBackground
    : palette.boxBackground;

  const entries = useMemo(() => {
    if (!liveLogs) return [];
    const all: Array<{
      id: string;
      deviceId: string;
      bumperNumber?: string;
      action: string;
      details: string;
      timestamp: number;
    }> = [];
    Object.values(devices).forEach((d) => {
      (d.data ?? []).forEach((entry) => {
        if (!entry) return;
        all.push({
          id: entry.id,
          deviceId: d.deviceId,
          bumperNumber: truckNames[d.deviceId],
          action: entry.action,
          details: entry.details,
          timestamp: entry.timestamp,
        });
      });
    });
    const sorted = all.sort((a, b) => b.timestamp - a.timestamp);
    if (selectedAsset === 'all') return sorted;
    return sorted.filter((e) => e.deviceId === selectedAsset);
  }, [devices, liveLogs, truckNames, selectedAsset]);

  const assetOptions = useMemo(
    () =>
      Object.entries(truckNames).map(([deviceId, name]) => ({
        deviceId,
        name,
      })),
    [truckNames]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          padding: '10px 14px',
          borderBottom: `1px solid ${borderCol}`,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 700,
            color: textPrimary,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
          }}
        >
          {t('dm_log_activity_logs')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Typography
            sx={{
              fontSize: '10px',
              fontWeight: 600,
              color: liveLogs ? '#4ade80' : textMuted,
              minWidth: '22px',
            }}
          >
            {liveLogs ? t('dm_log_live') : t('dm_log_off')}
          </Typography>
          <Switch
            size="small"
            checked={liveLogs}
            onChange={(e) => {
              onToggle(e.target.checked);
            }}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color: '#4ade80' },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                bgcolor: 'rgba(74,222,128,0.35)',
              },
            }}
          />
        </Box>
      </Box>

      {/* Asset filter */}
      <Box
        sx={{
          padding: '6px 14px',
          borderBottom: `1px solid ${borderCol}`,
          flexShrink: 0,
        }}
      >
        <Select
          size="small"
          value={selectedAsset}
          onChange={(e) => {
            setSelectedAsset(e.target.value);
          }}
          displayEmpty
          sx={{
            width: '100%',
            fontSize: '10px',
            height: '26px',
            color: textPrimary,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: borderCol },
            '& .MuiSvgIcon-root': { color: textMuted, fontSize: '16px' },
            '& .MuiSelect-select': { py: '3px', px: '8px' },
          }}
        >
          <MenuItem value="all" sx={{ fontSize: '10px' }}>
            {t('dm_log_all_assets')}
          </MenuItem>
          {assetOptions.map((opt) => (
            <MenuItem
              key={opt.deviceId}
              value={opt.deviceId}
              sx={{ fontSize: '10px' }}
            >
              {opt.name}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* Log list */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          padding: '8px',
          display: 'flex',
          flexDirection: 'column',
          gap: '5px',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': {
            background: borderCol,
            borderRadius: '2px',
          },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
        }}
      >
        {!liveLogs ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              gap: '10px',
              color: textMuted,
            }}
          >
            <Typography sx={{ fontSize: '26px', lineHeight: 1 }}>📋</Typography>
            <Typography
              sx={{ fontSize: '11px', textAlign: 'center', lineHeight: 1.5 }}
            >
              {t('dm_log_enable_hint')}
            </Typography>
          </Box>
        ) : entries.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: textMuted,
            }}
          >
            <Typography sx={{ fontSize: '11px' }}>
              {t('dm_log_no_logs')}
            </Typography>
          </Box>
        ) : (
          entries.map((entry) => {
            const cfg = ACTION_CONFIG[entry.action] ?? {
              labelKey: entry.action,
              color: '#9ca3af',
              bg: 'rgba(156,163,175,0.08)',
            };
            return (
              <Box
                key={entry.id}
                sx={{
                  background: cardBg,
                  border: `1px solid ${borderCol}`,
                  borderLeft: `3px solid ${cfg.color}`,
                  borderRadius: '5px',
                  padding: '7px 10px',
                  flexShrink: 0,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    mb: '4px',
                    flexWrap: 'wrap',
                  }}
                >
                  <Box
                    sx={{
                      background: cfg.bg,
                      color: cfg.color,
                      fontSize: '8px',
                      fontWeight: 700,
                      padding: '2px 6px',
                      borderRadius: '3px',
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t(cfg.labelKey)}
                  </Box>
                  {entry.bumperNumber && (
                    <Typography
                      sx={{
                        fontSize: '9px',
                        fontWeight: 600,
                        color: '#a5b4fc',
                      }}
                    >
                      {entry.bumperNumber}
                    </Typography>
                  )}
                  <Typography
                    sx={{
                      fontSize: '9px',
                      color: textMuted,
                      ml: 'auto',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatTs(entry.timestamp, timezone)}
                  </Typography>
                </Box>
                <Typography
                  sx={{ fontSize: '10px', color: textPrimary, lineHeight: 1.4 }}
                >
                  {entry.details}
                </Typography>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
};

export default LogsPanel;
